import { ScanMulti } from "../dashboard/llm/common/models";

interface CitationMapping {
    [citationId: string]: {
        data_type: string;
        item_key?: string;
        metadata?: Record<string, any>;
    };
}

/**
 * Generates a citation_mapping object from a ScanMulti object by traversing
 * all nested data structures and extracting _citation_id fields.
 */
export function generateCitationMapping(scanMulti: ScanMulti): CitationMapping {
    const mapping: CitationMapping = {};

    // Helper function to add to mapping
    const addMapping = (citationId: string, dataType: string, itemKey?: string) => {
        if (citationId && !mapping[citationId]) {
            mapping[citationId] = {
                data_type: dataType,
                ...(itemKey && { item_key: itemKey })
            };
        }
    };

    // Patient demographics
    if (scanMulti.patient?._citation_id) {
        addMapping(scanMulti.patient._citation_id, 'patient', 'patient');
    }

    // Laboratory results
    if (scanMulti.labs) {
        Object.entries(scanMulti.labs).forEach(([labName, labData]) => {
            if (labData?.results) {
                labData.results.forEach((result: any) => {
                    if (result._citation_id) {
                        addMapping(result._citation_id, 'labs', labName);
                    }
                });
            }
        });
    }

    // Imaging studies
    if (scanMulti.imaging) {
        scanMulti.imaging.forEach((img: any) => {
            if (img._citation_id) {
                addMapping(img._citation_id, 'imaging', img.study);
            }
        });
    }

    // Visits/Encounters
    if (scanMulti.visits) {
        scanMulti.visits.forEach((visit: any) => {
            if (visit._citation_id) {
                addMapping(visit._citation_id, 'visits', visit.csn);
            }
        });
    }

    // Hospital medications
    if (scanMulti.meds) {
        scanMulti.meds.forEach((med: any) => {
            if (med._citation_id) {
                addMapping(med._citation_id, 'meds', med.med_name);
            }
        });
    }

    // Orders
    if (scanMulti.orders) {
        scanMulti.orders.forEach((order: any) => {
            if (order._citation_id) {
                addMapping(order._citation_id, 'orders', order.name);
            }
        });
    }

    // High-frequency vital signs
    if (scanMulti.vs_1min) {
        scanMulti.vs_1min.forEach((vital: any) => {
            if (vital._citation_id) {
                addMapping(vital._citation_id, 'vs_1min', 'continuous_monitoring');
            }
        });
    }

    // Home medications
    if (scanMulti.home_meds) {
        scanMulti.home_meds.forEach((med: any) => {
            if (med._citation_id) {
                addMapping(med._citation_id, 'home_meds', med.med_subclass);
            }
        });
    }

    // Past medical history / diagnoses
    if (scanMulti.pmh) {
        scanMulti.pmh.forEach((dx: any) => {
            if (dx._citation_id) {
                addMapping(dx._citation_id, 'pmh', dx.desc10);
            }
        });
    }

    // Reason for visit
    if (scanMulti.reasons_for_visit?._citation_id) {
        addMapping(scanMulti.reasons_for_visit._citation_id, 'reasons_for_visit', 'chief_complaint');
    }

    // Echocardiogram studies
    if (scanMulti.echo) {
        scanMulti.echo.forEach((echo: any) => {
            if (echo._citation_id) {
                addMapping(echo._citation_id, 'echo', echo.display_name);
            }
        });
    }

    // 12-Lead ECG
    if (scanMulti.ecg_12L) {
        scanMulti.ecg_12L.forEach((ecg: any) => {
            if (ecg._citation_id) {
                addMapping(ecg._citation_id, 'ecg_12L', '12lead_ecg');
            }
        });
    }

    // Vital signs summary by CSN
    if (scanMulti.vs_stats_by_csn) {
        Object.entries(scanMulti.vs_stats_by_csn).forEach(([csn, stats]: [string, any]) => {
            if (stats._citation_id) {
                addMapping(stats._citation_id, 'vs_stats_by_csn', `${csn}_summary`);
            }
        });
    }

    // Continuous ECG rhythm summary
    if (scanMulti.ecg_continuous_stats?._citation_id) {
        addMapping(
            scanMulti.ecg_continuous_stats._citation_id,
            'ecg_continuous_stats',
            'continuous_ecg_summary'
        );
    }

    // Individual rhythm episodes
    if (scanMulti.ecg_continuous_granular) {
        scanMulti.ecg_continuous_granular.forEach((rhythm: any) => {
            if (rhythm._citation_id) {
                addMapping(rhythm._citation_id, 'ecg_continuous_granular', rhythm.segment_rhythm);
            }
        });
    }

    // Risk predictions
    if (scanMulti.predictions) {
        Object.entries(scanMulti.predictions).forEach(([predKey, prediction]: [string, any]) => {
            if (prediction._citation_id) {
                addMapping(prediction._citation_id, 'predictions', predKey);
            }
            
            // Risk features within predictions
            if (prediction.top_features) {
                prediction.top_features.forEach((feature: any) => {
                    if (feature._citation_id) {
                        addMapping(feature._citation_id, 'predictions', feature.name);
                    }
                });
            }
        });
    }

    // Clinical notes
    if (scanMulti.notes) {
        Object.entries(scanMulti.notes).forEach(([noteType, notes]: [string, any]) => {
            if (Array.isArray(notes)) {
                notes.forEach((note: any) => {
                    if (note._citation_id) {
                        addMapping(note._citation_id, 'notes', noteType);
                    }
                });
            }
        });
    }

    return mapping;
}
